import React, { useMemo, useState } from "react";
import { Button, Header, Icon } from "semantic-ui-react";
import useCategoriesTree, {
  invalidateCategoriesTree,
} from "../../hooks/useCategoriesTree";
import {
  deleteCategory,
  reorderCategories,
  updateCategory,
} from "../../services/categoriesApi";
import { renderCategoryIcon } from "../../datas/categoryIcons";
import "./categories.css";

function findNode(nodes, id) {
  for (const n of nodes) {
    if (n._id === id) return n;
    const sub = findNode(n.children || [], id);
    if (sub) return sub;
  }
  return null;
}

const AdminCategories = ({
  setAppMessage,
  setOpenEditModal,
  setEditTarget,
  setOpenCreateModal,
  setCreateParent,
}) => {
  const { tree, loading } = useCategoriesTree();
  const [path, setPath] = useState([]);

  const currentList = useMemo(() => {
    if (path.length === 0) return tree;
    const node = findNode(tree, path[path.length - 1]);
    return node?.children || [];
  }, [tree, path]);

  const breadcrumb = useMemo(() => {
    const trail = [{ id: null, name: "Racine" }];
    let nodes = tree;
    for (const id of path) {
      const node = findNode(nodes, id);
      if (!node) break;
      trail.push({ id: node._id, name: node.name });
      nodes = node.children || [];
    }
    return trail;
  }, [tree, path]);

  const goTo = (idx) => setPath(path.slice(0, idx));

  const handleSwap = async (i, dir, list) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    const a = list[i];
    const b = list[j];
    try {
      await reorderCategories([
        { id: a._id, order: b.order },
        { id: b._id, order: a.order },
      ]);
      invalidateCategoriesTree();
    } catch (e) {
      setAppMessage({ success: false, message: "Erreur lors du reorder" });
    }
  };

  const handleToggleVisible = async (cat) => {
    try {
      await updateCategory(cat._id, { visible: !cat.visible });
      invalidateCategoriesTree();
    } catch (e) {
      setAppMessage({ success: false, message: "Erreur visibilité" });
    }
  };

  const handleDelete = async (cat) => {
    if (!window.confirm(`Supprimer "${cat.name}" ?`)) return;
    try {
      await deleteCategory(cat._id);
      invalidateCategoriesTree();
    } catch (e) {
      setAppMessage({
        success: false,
        message: e.response?.data?.message || "Erreur suppression",
      });
    }
  };

  const handleEdit = (cat) => {
    setEditTarget(cat);
    setOpenEditModal(true);
  };

  const handleCycleAvailability = async (cat) => {
    const cycle = { always: "lunch", lunch: "dinner", dinner: "always" };
    const next = cycle[cat.availability || "always"];
    try {
      await updateCategory(cat._id, { availability: next });
      invalidateCategoriesTree();
    } catch (e) {
      setAppMessage({ success: false, message: "Erreur availability" });
    }
  };

  const handleCreate = () => {
    setCreateParent(path.length ? path[path.length - 1] : null);
    setOpenCreateModal(true);
  };

  if (loading && tree.length === 0) {
    return <div className="admin-cat-page">Chargement…</div>;
  }

  const sorted = currentList.slice().sort((a, b) => a.order - b.order);

  return (
    <div className="admin-cat-page">
      <Header as="h2" inverted>Gestion des catégories</Header>

      <div className="admin-cat-breadcrumb">
        {breadcrumb.map((b, idx) => (
          <span key={b.id || "root"}>
            {idx > 0 && " › "}
            <a onClick={() => goTo(idx)}>{b.name}</a>
          </span>
        ))}
      </div>

      {sorted.map((cat, i, arr) => {
        const isTopLevel = !cat.parentId;
        const av = cat.availability || "always";
        const avMeta = {
          always: { label: "Toujours", className: "av-always" },
          lunch: { label: "Midi", className: "av-lunch" },
          dinner: { label: "Soir", className: "av-dinner" },
        }[av];
        return (
        <div
          key={cat._id}
          className={`admin-cat-row ${cat.visible ? "" : "invisible"}`}
        >
          <span style={{ width: 40 }}>
            {renderCategoryIcon(cat.icon, cat.iconColor || "white", "1x", cat.badge)}
          </span>
          <span className="name" onClick={() => setPath([...path, cat._id])}>
            {cat.name} <small style={{ opacity: 0.6 }}>/{cat.slug}</small>
          </span>
          {isTopLevel && (
            <span
              className={`av-pill ${avMeta.className}`}
              title="Cliquer pour cycler Toujours → Midi → Soir"
              onClick={() => handleCycleAvailability(cat)}
            >
              {avMeta.label}
            </span>
          )}
          <div className="actions">
            <Button
              icon
              size="mini"
              disabled={i === 0}
              onClick={() => handleSwap(i, -1, arr)}
            >
              <Icon name="arrow up" />
            </Button>
            <Button
              icon
              size="mini"
              disabled={i === arr.length - 1}
              onClick={() => handleSwap(i, 1, arr)}
            >
              <Icon name="arrow down" />
            </Button>
            <Button
              icon
              size="mini"
              color={cat.visible ? "green" : "grey"}
              title={cat.visible ? "Masquer" : "Rendre visible"}
              onClick={() => handleToggleVisible(cat)}
            >
              <Icon name={cat.visible ? "eye" : "eye slash"} />
            </Button>
            <Button
              icon
              size="mini"
              color="blue"
              onClick={() => handleEdit(cat)}
            >
              <Icon name="edit" />
            </Button>
            <Button
              icon
              size="mini"
              color="red"
              onClick={() => handleDelete(cat)}
            >
              <Icon name="trash" />
            </Button>
          </div>
        </div>
        );
      })}

      <Button primary className="admin-cat-add" onClick={handleCreate}>
        <Icon name="plus" /> Ajouter une catégorie ici
      </Button>
    </div>
  );
};

export default AdminCategories;
